<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { ChallengeConfiguration } from "@/types/v2/task/challenge/configuration.types";
import { useChallenge } from "@/composables/tasks/challenge/useChallenge";
import { useChallengeConfigurations } from "@/composables/tasks/challenge/useChallengeConfigurations";
import { useTask } from "@/composables/tasks/useTask";
import { useNotifications } from "@/composables/useNotifications";
import { TasksService } from "@/services/v2/tasks";
import { useEquipment } from "@/composables/tasks/useEquipment";

import FooterActions from "@/components/v2/Tasks/Challenge/Buttons/ActionButtons/FooterActions.vue";
import ConfigurationsTable from "@/components/v2/Tasks/Challenge/Tables/ConfigurationsTable.vue";
import BathConfigurationsTable from "@/components/v2/Tasks/Challenge/Tables/BathConfigurationsTable.vue";
import HistoryList from "@/components/v2/Tasks/Challenge/Lists/HistoryList.vue";
import CommentsModal from "@/components/v2/Modal/CommentsModal.vue";
import InstructionsModal from "@/components/v2/Modal/InstructionsModal.vue";
import WeightModal from "@/components/v2/Tasks/Challenge/Modals/WeightModal.vue";
import WeightIcon from "@/components/v2/UI/Icons/WeightIcon.vue";
import DocumentIcon from "@/components/v2/UI/Icons/DocumentIcon.vue";
import InfoIcon from "@/components/v2/UI/Icons/InfoIcon.vue";
import { useWeight } from "@/composables/tasks/challenge/useChallengeWeight";
import RandomWeightProgressCard from "@/components/v2/Tasks/Shared/RandomWeightProgressCard.vue";
import TaskCompletionBlockedModal from "@/components/v2/Tasks/Task/TaskCompletionBlockedModal.vue";

// ===================================================================
// Composables
// ===================================================================
const router = useRouter();
const { activeTask } = useTask();
const { 
    tanks, 
    isLoading, 
    error, 
    ensureTaskLoaded,
    vaccineMethods, 
    challengeMaterials, 
    dilutions, 
    preChallengeTask,
 
} = useChallenge();
const {
    configurations,
    standardConfigurations,
    bathConfigurations,
    isBathTask,
    isStandardTask,
    isMixedTask,

} = useChallengeConfigurations();
const { showTaskCompletionSuccess, showError } = useNotifications();
const {
    isIndividualWeightType,
    isNoWeightType,
    isRandomWeightType,
    getWeighedFish,
    getTargetWeightAmount
} = useWeight();
const { getWeightIdCompletionValidation } = useEquipment();

// ===================================================================
// Modal State
// ===================================================================
const selectedConfiguration = ref<ChallengeConfiguration | null>(null);
const weightModal = ref<boolean>(false);

// ===================================================================
// Modal Handlers
// ===================================================================
const openWeightModal = (config?: ChallengeConfiguration | null) => {
    weightModal.value = true;
    selectedConfiguration.value = config || null;
};

const goToEquipmentManagement = () => {
    router.push({ name: "ChallengeEquipmentView" });
};

// ===================================================================
// Computed Properties
// ===================================================================
const taskTypeLabel = computed(() => {
    if (isMixedTask.value) return 'Mixed Challenge (Bath + Injection)';
    if (isBathTask.value) return 'Bath Challenge';
    return 'Injection Challenge';
});

const taskDescription = computed(() => {
    if (isBathTask.value) {
        return 'Review bath challenge configurations. Processes are time-based with specified durations. You can start and finish each bath process individually.';
    }
    return 'You can make minor adjustments to tag colors, destination tanks, and destination groups if needed. All changes require justification before proceeding.';
});

// ===================================================================
// State
// ===================================================================
const instructionsModal = ref(false);
const commentsModal = ref(false);
const isFinishing = ref(false);
const completionBlockModal = ref(false);

// ===================================================================
// Error Handling
// ===================================================================
const handleRetry = () => {
    ensureTaskLoaded();
};

// ===================================================================
// Actions
// ===================================================================
// Open instructions modal
const toggleInstructionsModal = () => {
    instructionsModal.value = !instructionsModal.value;
};

// Open comments modal
const toggleCommentsModal = () => {
    commentsModal.value = !commentsModal.value;
};

// Next step
const handleProceed = () => {
    if (isBathTask.value) {
        // For bath tasks, check if all processes are started/completed
        // You might want different logic here
        router.push({ name: 'ChallengeChallenges' });
    } else {
        router.push({ name: 'ChallengeChallenges' });
    }
};

// Check if we can proceed (for bath tasks, might want to check process status)
const canProceed = computed(() => {
    if (isBathTask.value) {
        // For demo purposes, always allow proceed
        // In real implementation, you might check if processes are ready
        return true;
    }
    return tanks.value?.length > 0;
});
const completionValidation = computed(() => getWeightIdCompletionValidation());



// Handle finishing the task
const handleFinishTask = async () => {
    if (!activeTask.value) return;

    const validation = getWeightIdCompletionValidation();
    if (!validation.isValid) {
        completionBlockModal.value = true;
        return;
    }
    
    isFinishing.value = true;
    
    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
        console.error('Error completing challenge task:', error);
        showError("Task Completion Failed", "Failed to complete the challenge task. Please try again.");
    } finally {
        isFinishing.value = false;
    }
};
</script>

<template>
    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center h-full">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex flex-col justify-center items-center h-full">
        <div class="text-red-500 mb-4">{{ error }}</div>
        <button @click="handleRetry" class="px-4 py-2 bg-primary text-white rounded">Retry</button>
    </div>

    <!-- Main Content -->
    <div v-else class="flex flex-col h-full justify-between">
        <div class="space-y-6">
            <!-- Header Actions -->
            <div class="flex justify-between items-start mb-2 w-full">
                <div class="space-y-2">
                    <h1 class="text-2xl font-bold text-gray-800">{{ activeTask?.title }}</h1>
                    <div class="flex items-center space-x-2">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            :class="{
                            'bg-blue-100 text-blue-800': isBathTask,
                            'bg-orange-100 text-orange-800': isStandardTask && !isBathTask,
                            'bg-purple-100 text-purple-800': isMixedTask
                            }">
                        {{ taskTypeLabel }}
                    </span>
                        <span v-if="configurations.length > 0" class="text-sm text-gray-500">
                {{ configurations.length }} configuration{{ configurations.length !== 1 ? 's' : '' }}
              </span>
                    </div>
                </div>
                <div v-if="isBathTask || isMixedTask" class="flex justify-end gap-4">
                    <button
                        @click="goToEquipmentManagement"
                        class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
                        <InfoIcon />
                        <span>Equipment</span>
                    </button>

                    <button
                        v-if="!isIndividualWeightType && !isNoWeightType"
                        @click="openWeightModal()"
                        class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
                        <WeightIcon />
                        Record Weight
                    </button>
                    <button
                        @click="router.push({name: 'ChallengeWeights'})"
                        class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
                        <DocumentIcon />
                        View Weight Records
                    </button>
                </div>
            </div>

            <RandomWeightProgressCard
                v-if="isRandomWeightType"
                class="sticky top-2 z-10"
                :current="getWeighedFish"
                :target="getTargetWeightAmount"
            />

            <span class="w-full bg-secondary h-[1px] block"></span>

            <!-- Subtitle & Description -->
            <div class="mb-6">
                <h2 class="text-xl font-bold mb-2">
                    {{ isBathTask ? 'Bath Process Management' : 'Editing before starting task' }}
                </h2>
                <p class="text-sm text-gray-600">
                    {{ taskDescription }}
                </p>
            </div>

            <!-- Pre-challenge task reference if applicable -->
            <div v-if="preChallengeTask" class="p-4 bg-blue-50 rounded-md border border-blue-200">
                <h3 class="text-lg font-medium text-blue-800">Dilutions Task</h3>
                <p class="text-sm text-blue-700">
                    This challenge is linked to dilutions task: {{ preChallengeTask.title }}
                    <span class="ml-2 px-2 py-1 text-xs rounded-full"
                          :class="{
                          'bg-green-100 text-green-800': preChallengeTask.status === 'completed',
                          'bg-yellow-100 text-yellow-800': preChallengeTask.status === 'in_progress',
                          'bg-gray-100 text-gray-800': preChallengeTask.status === 'pending'
                        }">
                    {{ preChallengeTask.status }}
                  </span>
                </p>
            </div>

            <!-- History -->
            <HistoryList />

            <!-- Mixed Task - Show Both Tables -->
            <div v-if="isMixedTask">
                <!-- Standard Configurations -->
                <div v-if="standardConfigurations.length > 0" class="mb-8">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">Injection Configurations</h3>
                    <ConfigurationsTable
                        :configurations="standardConfigurations"
                        :vaccineMethods="vaccineMethods"
                        :challengeMaterials="challengeMaterials"
                        :dilutions="dilutions"
                        :tanks="tanks"
                    />
                </div>

                <!-- Bath Configurations -->
                <div v-if="bathConfigurations.length > 0">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">Bath Configurations</h3>
                    <BathConfigurationsTable
                        :configurations="bathConfigurations"
                        :vaccineMethods="vaccineMethods"
                        :tanks="tanks"
                        @openWeightModal="openWeightModal"
                    />
                </div>
            </div>

            <!-- Pure Bath Task -->
            <BathConfigurationsTable
                v-else-if="isBathTask && tanks?.length"
                :configurations="bathConfigurations"
                :vaccineMethods="vaccineMethods"
                :tanks="tanks"
                @openWeightModal="openWeightModal"
            />

            <!-- Pure Standard Task -->
            <ConfigurationsTable
                v-else-if="isStandardTask && tanks?.length"
                :configurations="standardConfigurations"
                :vaccineMethods="vaccineMethods"
                :challengeMaterials="challengeMaterials"
                :dilutions="dilutions"
                :tanks="tanks"
            />

            <!-- No configurations -->
            <div v-else class="text-center py-8 text-gray-500">
                <p>No configurations available</p>
            </div>
        </div>

        <FooterActions>
            <template #start>
                <button
                    @click="toggleInstructionsModal"
                    class="w-full"
                >
                    Instructions
                </button>
            </template>

            <template #middle>
                <button
                    @click="toggleCommentsModal"
                    class="w-full"
                >
                    Comments
                </button>
            </template>

            <template #end>
                <button
                    v-if="!isBathTask"
                    @click="handleProceed"
                    :disabled="!canProceed"
                    class="w-full"
                    :class="{
                'opacity-50 cursor-not-allowed': !canProceed
            }"
                >
                    Proceed
                </button>

                <div v-else class="w-full text-center">
                    <button
                        @click="handleFinishTask"
                        :disabled="isFinishing"
                        class="w-full relative"
                        :class="{ 'opacity-50 cursor-not-allowed': isFinishing }"
                    >
                        <span v-if="isFinishing" class="flex items-center justify-center">
                            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Finishing...
                        </span>
                        <span v-else>
                            Finish Task
                        </span>
                    </button>
                </div>
            </template>
        </FooterActions>

        <!-- Modals -->
        <InstructionsModal :show-modal="instructionsModal" :toggle-instructions-modal="toggleInstructionsModal" />
        <CommentsModal :show-modal="commentsModal" :toggle-comments-modal="toggleCommentsModal" />
        
        <!-- Weight Modal -->
        <WeightModal
            v-if="weightModal"
            :show="weightModal"
            :configuration="selectedConfiguration"
            @close="weightModal = false"
        />
        <TaskCompletionBlockedModal
            :show="completionBlockModal"
            :reason="completionValidation.reason"
            @close="completionBlockModal = false"
        />
    </div>
</template>
